import { makeChanged, mergeChangedGroup } from '@/components/profile';

// Groups the server replaces wholesale on save ($set of the whole object).
const WHOLE_GROUPS = [
  'generalData',
  'employmentSettings',
  'allowances',
  'otherAllowances',
  'additionalPersonalInfo',
];

/**
 * Request body for saving from the employee profile: only the groups that
 * changed, so a save without changes leaves the document untouched.
 *
 * @param {Object} params
 * @param {Object} params.payload - what the classic form would send
 * @param {Object} params.current - form.getFieldsValue(true)
 * @param {Object} params.base - form values as loaded
 * @param {Object} params.serverData - the employee as the server returned it
 * @returns {{ body: Object, hasChanges: boolean }}
 */
export default function buildEmployeeProfileBody({
  payload,
  current,
  base,
  serverData,
}) {
  const changed = makeChanged(current, base);

  // Company and sector always go along when the form has them: the server
  // needs the sector to update its HR officers when isHrOfficer changes, and
  // re-sending the same ids changes nothing. (A company without sectors
  // renders no sector field.)
  const body = {};
  ['company', 'employeeSector'].forEach((key) => {
    if (payload[key] !== undefined) body[key] = payload[key];
  });
  // Name changes also update the linked user account, so only when changed.
  ['firstName', 'lastName', 'status'].forEach((key) => {
    if (changed(key)) body[key] = payload[key];
  });
  WHOLE_GROUPS.forEach((group) => {
    if (changed(group)) {
      body[group] = mergeChangedGroup(serverData?.[group], payload[group]);
    }
  });
  if (changed('specialAbsence')) {
    body['specialAbsence.enabled'] = payload['specialAbsence.enabled'];
    body['specialAbsence.limits'] = payload['specialAbsence.limits'];
  }
  // The holiday snapshot follows the employment dates it is computed from.
  if (changed('holidayLeaveCalculation') || changed('employmentSettings')) {
    body.holidayLeaveCalculation = payload.holidayLeaveCalculation;
  }
  if (payload.effectiveDate) body.effectiveDate = payload.effectiveDate;

  const hasFieldChanges = Object.keys(body).some(
    (key) => !['company', 'employeeSector'].includes(key)
  );
  const idsChanged = changed('company') || changed('employeeSector');

  return { body, hasChanges: hasFieldChanges || idsChanged };
}
